<script setup lang="ts">
import {HTMLAttributes, nextTick, onMounted, onUnmounted, ref, render, type VNode, watch} from 'vue';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {LatLng, MapLocation} from "@/shared/graphql/types";
import {useI18n} from "vue-i18n";
import {Locale} from "@/shared/i18n/i18n.ts";

const {t, locale} = useI18n();

const lang = ref(Locale.fromString(locale.value.toString()).language);

export type MarkerContent = {
  content: string | VNode;
  width: string;
}

interface Props {
  pointsOfInterest?: any[];
  activePointOfInterest?: any;
  showControls?: boolean;
  class?: HTMLAttributes['class'];
  poiMapLocation?: (poi: any) => MapLocation;
  poiTooltip?: (poi: any) => string;
  poiMarker?: (poi: any) => Promise<MarkerContent> | MarkerContent;
  markerColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
  showControls: true,
  markerColor: '#3388ff'
});

const emit = defineEmits(['update:activePointOfInterest']);

const mapContainer = ref<HTMLElement | null>(null);
const map = ref<L.Map | null>(null);
const markersMap = new Map<string, L.CircleMarker>();
const showLoadingIndicator = ref(false);

const isValidLocation = (latLng?: LatLng): boolean => {
  return !!(
    latLng &&
    !isNaN(latLng.latitude) &&
    !isNaN(latLng.longitude) &&
    isFinite(latLng.latitude) &&
    isFinite(latLng.longitude)
  );
};

const calculateBounds = (points: any[]): L.LatLngBounds | null => {
  if (!points?.length) return null;
  const { minLat, maxLat, minLon, maxLon } = points.reduce((acc, mapLocation) => {
    const { latitude, longitude } = mapLocation.latLng;
    acc.minLat = Math.min(acc.minLat, latitude);
    acc.maxLat = Math.max(acc.maxLat, latitude);
    acc.minLon = Math.min(acc.minLon, longitude);
    acc.maxLon = Math.max(acc.maxLon, longitude);
    return acc;
  }, { minLat: 90, maxLat: -90, minLon: 180, maxLon: -180 });

  return minLat === 90 || maxLat === -90 || minLon === 180 || maxLon === -180
    ? null
    : L.latLngBounds([minLat, minLon], [maxLat, maxLon]);
};

const createCircleMarker = (latLng: L.LatLngExpression, color: string): L.CircleMarker => {
  return L.circleMarker(latLng, {
    color,
    radius: 7
  });
};

const setupMarkerEvents = (marker: L.CircleMarker, mapLocation: MapLocation) => {
  if (props.poiTooltip) {
    const tooltip = props.poiTooltip(mapLocation);
    if (tooltip) {
      marker.bindTooltip(tooltip);
    }
  } else {
    marker.bindTooltip(mapLocation.tooltip);
  }

  marker.on('click', async () => {
    emit('update:activePointOfInterest', mapLocation);

    if (props.poiMarker) {
      showLoadingIndicator.value = true;
      try {
        const content = await props.poiMarker(mapLocation);
        updatePopupContent(marker.getPopup()!, content, marker);
      } finally {
        showLoadingIndicator.value = false;
      }
    }
  });
};

const updatePopupContent = (popup: L.Popup, popupContent: MarkerContent, marker: L.CircleMarker) => {
  if (!popup) return;

  if (typeof popupContent.content === 'string') {
    popup.setContent(popupContent.content);
    return;
  }
  //@ts-ignore
  const container = marker.popupContainer || document.createElement('div');
  if (popupContent.width) {
    container.style.width = `${popupContent.width}px`;
  }
  //@ts-ignore
  marker.popupContainer = container;
  //@ts-ignore
  render(popupContent.content, container);
  popup.setContent(container);
};

const initMapMarkers = async () => {
  await nextTick();
  if (!map.value || !props.pointsOfInterest?.length) return;

  clearMap();

  const validPoints = props.pointsOfInterest.map(poi => {
    if (props.poiMapLocation) {
      const mapLocation = props.poiMapLocation(poi);
      return mapLocation && isValidLocation(mapLocation?.latLng) ? mapLocation : null;
    } else {
      return poi && isValidLocation(poi?.latLng) ? poi : null;
    }
  }).filter(Boolean);

  validPoints.forEach((mapLocation) => {
    const circleMarker = createCircleMarker(
      [mapLocation.latLng.latitude, mapLocation.latLng.longitude],
      props.markerColor
    );

    if (props.poiMarker) {
      circleMarker.bindPopup(`<p style="text-align: center;margin-top: 40px !important; font-size: 14px;">${t('shared.common.loading')}</p>`);
    }

    setupMarkerEvents(circleMarker, mapLocation);
    markersMap.set(mapLocation.id, circleMarker);
    map.value?.addLayer(circleMarker);
  });

  const bounds = calculateBounds(validPoints);
  if (bounds) {
    map.value?.fitBounds(bounds, { maxZoom: 9, animate: false });
  } else {
    map.value?.setView([50.8503, 4.3517], 9);
  }
};

const clearMap = () => {
  markersMap.forEach(marker => {
    marker.remove();
    //@ts-ignore
    if (marker.popupContainer) {
      //@ts-ignore
      render(null, marker.popupContainer);
      //@ts-ignore
      marker.popupContainer = null;
    }
  });

  markersMap.clear();
};

onMounted(() => {
  renderMap();
});

const renderMap = () => {
  if (!mapContainer.value) return;

  if (map.value) {
    map.value.remove();
  }

  map.value = L.map(mapContainer.value, {
    center: [50.8503, 4.3517],
    zoom: 7,
    maxZoom: 18,
    worldCopyJump: true,
    preferCanvas: true,
    zoomAnimation: true,
    markerZoomAnimation: true,
    fadeAnimation: true
  });

  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
    maxZoom: 18,
  }).addTo(
    //@ts-ignore
    map.value
  );

  initMapMarkers();
}

onUnmounted(() => {
  clearMap();
  map.value?.remove();
  map.value = null;
});

watch(() => props.pointsOfInterest, () => {
  renderMap();
}, {deep: true});

watch(() => locale.value, () => {
  renderMap();
}, {deep: true});

watch(() => props.activePointOfInterest, (newPoi, oldPoi) => {
  if (!map.value) return;

  const newMapLocation = newPoi ? props.poiMapLocation(newPoi) : null;
  const oldMapLocation = oldPoi ? props.poiMapLocation(oldPoi) : null;

  if (oldMapLocation) {
    const oldMarker = markersMap.get(oldMapLocation.id);
    if (oldMarker) {
      oldMarker.setStyle({color: props.markerColor});
    }
  }

  if (newMapLocation) {
    const newMarker = markersMap.get(newMapLocation.id);
    if (newMarker) {
      newMarker.setStyle({color: props.markerColor});

      if (!map.value.getBounds().contains(newMarker.getLatLng())) {
        map.value.setView(newMarker.getLatLng(), Math.max(14, map.value.getZoom()), {
          animate: true
        });
      }
    }
  }
});
</script>

<template>
  <div ref="mapContainer" class="map-container" :class="props.class">
    <template v-if="showLoadingIndicator">
      <div class="leaflet-loading">
      </div>
    </template>
  </div>
</template>

<style scoped>
.map-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 100%;
  max-height: 100%;
  overflow: hidden;
}

.leaflet-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
}
</style>